/**
 * Default century pivot for 2-digit-year input standards (E-025). A 2-digit
 * year < pivot maps to the 2000s; >= pivot maps to the 1900s. Default 50:
 * 00–49 → 2000–2049, 50–99 → 1950–1999. Configurable via the optional
 * `pivot` arg threaded through the date parsers.
 */
export const DEFAULT_CENTURY_PIVOT = 50;
/** Stored `keyStandard` value for the "Other (custom pattern)" option (E-025).
 *  The actual pattern is appended as `CUSTOM:<PATTERN>` (e.g. `CUSTOM:YYYYJJJ`);
 *  this bare value is the enum/select option key. */
export const CUSTOM_DATE_PATTERN_VALUE = "CUSTOM";
/** Prefix on a stored `keyStandard` that carries a custom token pattern. */
const CUSTOM_PREFIX = `${CUSTOM_DATE_PATTERN_VALUE}:`;
/** The ONLY tokens the custom date pattern grammar accepts (E-025). Free-text
 *  is never shipped — every pattern is validated against this closed vocabulary
 *  and rejected if it contains anything else or is ambiguous/under-specified. */
export const CUSTOM_DATE_TOKENS = ["YYYY", "YY", "MM", "DD", "JJJ", "C"];
/**
 * Supported per-field date INPUT key standards (E-021 / E-025). Single source
 * of truth for the values `canonicalizeDate` understands — the field-editor
 * input-format picker reads this list rather than hardcoding a parallel one.
 * Each entry's `value` is the stored `keyStandard`; `label` is the
 * operator-facing form.
 *
 * Separator vs separatorless share a `value` family because `canonicalizeDate`
 * only cares about digit ORDER, not separators — it strips non-digits before
 * parsing. So "MM/DD/YYYY" and "MMDDYYYY" both canonicalize identically; the
 * label distinction is purely guidance for the operator.
 *
 * E-025 expands the curated set with 2-digit-year, century-flag, and Julian
 * forms, plus an "Other (custom pattern)" escape hatch (validated against the
 * token grammar — never raw free-text).
 */
export const DATE_KEY_STANDARDS = [
    { value: "MMDDYYYY", label: "MMDDYYYY (no separators — fast keying)" },
    { value: "DDMMYYYY", label: "DDMMYYYY (no separators — fast keying)" },
    { value: "YYYYMMDD", label: "YYYYMMDD (no separators — fast keying)" },
    { value: "MMDDYY", label: "MMDDYY (2-digit year)" },
    { value: "YYMMDD", label: "YYMMDD (2-digit year)" },
    { value: "DDMMYY", label: "DDMMYY (2-digit year)" },
    { value: "CYYMMDD", label: "CYYMMDD (century flag: 0=19xx, 1=20xx)" },
    { value: "YYJJJ", label: "YYJJJ (Julian — 2-digit year + day-of-year)" },
    { value: "YYYYJJJ", label: "YYYYJJJ (Julian — 4-digit year + day-of-year)" },
    { value: CUSTOM_DATE_PATTERN_VALUE, label: "Other (custom pattern)…" },
];
export function canonicalFamily(dataType) {
    switch ((dataType ?? "").toLowerCase()) {
        case "date":
            return "date";
        case "currency":
        case "decimal":
        case "number":
        case "integer":
            return "decimal";
        default:
            return "none";
    }
}
/** Canonicalize a procedurally-keyed value to its canonical string, or null if unparseable
 *  / outside a canonical family. Date → ISO yyyy-MM-dd; decimal → plain decimal string
 *  (compared numerically downstream, so leading zeros / trailing zeros are immaterial). */
export function canonicalizeKeyedValue(raw, dataType, keyStandard) {
    if (raw == null)
        return null;
    const v = raw.trim();
    if (v.length === 0)
        return null;
    switch (canonicalFamily(dataType)) {
        case "date":
            return canonicalizeDate(v, keyStandard);
        case "decimal":
            return canonicalizeDecimal(v, keyStandard);
        default:
            return null;
    }
}
// ── Date parsing primitives (E-025) ──
/** Map a 2-digit year to a full year using the century pivot. */
function pivotYear(yy, pivot) {
    return yy < pivot ? 2000 + yy : 1900 + yy;
}
/** Assemble + calendar-validate a (year, month, day) triple → ISO yyyy-MM-dd,
 *  or null if the date is impossible (Feb 31, Apr 31, …). Single round-trip
 *  home used by every month/day parser. */
function assembleIso(year, m, d) {
    if (!Number.isInteger(year) || year < 1 || year > 9999)
        return null;
    if (m < 1 || m > 12 || d < 1 || d > 31)
        return null;
    const yyyy = String(year).padStart(4, "0");
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
    if (Number.isNaN(dt.getTime()) || dt.getUTCMonth() + 1 !== m || dt.getUTCDate() !== d) {
        return null;
    }
    return `${yyyy}-${mm}-${dd}`;
}
/** Convert a (full year, day-of-year 1..366) Julian pair → ISO, validating the
 *  day-of-year against the year's actual length (366 only in a leap year). */
function julianToIso(year, doy) {
    if (!Number.isInteger(year) || year < 1 || year > 9999)
        return null;
    if (doy < 1 || doy > 366)
        return null;
    // Jan 1 + (doy-1) days, in UTC. Then verify it didn't roll into the next year.
    const dt = new Date(Date.UTC(year, 0, 1));
    dt.setUTCDate(dt.getUTCDate() + (doy - 1));
    if (dt.getUTCFullYear() !== year)
        return null; // doy too large for this year
    return assembleIso(year, dt.getUTCMonth() + 1, dt.getUTCDate());
}
const num = (s) => Number(s);
/** Per-standard dispatch table (replaces the old 8-digit hard gate). */
const DATE_STANDARDS = {
    MMDDYYYY: {
        expectedDigits: 8,
        parse: (g) => assembleIso(num(g.slice(4)), num(g.slice(0, 2)), num(g.slice(2, 4))),
    },
    DDMMYYYY: {
        expectedDigits: 8,
        parse: (g) => assembleIso(num(g.slice(4)), num(g.slice(2, 4)), num(g.slice(0, 2))),
    },
    YYYYMMDD: {
        expectedDigits: 8,
        parse: (g) => assembleIso(num(g.slice(0, 4)), num(g.slice(4, 6)), num(g.slice(6))),
    },
    MMDDYY: {
        expectedDigits: 6,
        parse: (g, p) => assembleIso(pivotYear(num(g.slice(4)), p), num(g.slice(0, 2)), num(g.slice(2, 4))),
    },
    YYMMDD: {
        expectedDigits: 6,
        parse: (g, p) => assembleIso(pivotYear(num(g.slice(0, 2)), p), num(g.slice(2, 4)), num(g.slice(4))),
    },
    DDMMYY: {
        expectedDigits: 6,
        parse: (g, p) => assembleIso(pivotYear(num(g.slice(4)), p), num(g.slice(2, 4)), num(g.slice(0, 2))),
    },
    CYYMMDD: {
        expectedDigits: 7,
        parse: (g) => {
            const c = g[0];
            if (c !== "0" && c !== "1")
                return null; // century flag: 0=19xx, 1=20xx
            const year = (c === "1" ? 2000 : 1900) + num(g.slice(1, 3));
            return assembleIso(year, num(g.slice(3, 5)), num(g.slice(5)));
        },
    },
    YYJJJ: {
        expectedDigits: 5,
        parse: (g, p) => julianToIso(pivotYear(num(g.slice(0, 2)), p), num(g.slice(2))),
    },
    YYYYJJJ: {
        expectedDigits: 7,
        parse: (g) => julianToIso(num(g.slice(0, 4)), num(g.slice(4))),
    },
};
function canonicalizeDate(v, std, pivot = DEFAULT_CENTURY_PIVOT) {
    // E-025 — "Other (custom pattern)": a stored `CUSTOM:<PATTERN>` keyStandard
    // routes to the token-grammar parser (the pattern is validated there).
    if (std && std.startsWith(CUSTOM_PREFIX)) {
        return parseCustomDatePattern(v, std.slice(CUSTOM_PREFIX.length), pivot);
    }
    const digits = v.replace(/\D/g, "");
    // Unknown standard → fall back to the curated default (MMDDYYYY), matching
    // the prior switch's `default` arm so legacy callers keep their behavior.
    const def = (std ? DATE_STANDARDS[std] : undefined) ?? DATE_STANDARDS.MMDDYYYY;
    if (digits.length !== def.expectedDigits)
        return null;
    return def.parse(digits, pivot);
}
const TOKEN_WIDTH = {
    YYYY: 4, YY: 2, MM: 2, DD: 2, JJJ: 3, C: 1,
};
/**
 * Tokenize + validate a custom date pattern against the closed token grammar
 * (`YYYY YY MM DD JJJ C`). Returns a parse plan, or null when the pattern:
 *   - contains any token outside the vocabulary,
 *   - is empty/blank,
 *   - is ambiguous (a component token appears more than once),
 *   - has no year source, or both YYYY and YY (or YY without a C — see below),
 *   - mixes Julian (JJJ) with MM/DD (over-specified), or
 *   - has neither a day-of-month (DD) nor a day-of-year (JJJ).
 * Free-text never bypasses this — the grammar is the only accepted input.
 */
function planCustomPattern(pattern) {
    const p = pattern.trim();
    if (p.length === 0)
        return null;
    const slices = [];
    let i = 0;
    let offset = 0;
    while (i < p.length) {
        // Longest-match the token vocabulary (YYYY before YY).
        const token = CUSTOM_DATE_TOKENS.find((t) => p.startsWith(t, i));
        if (!token)
            return null; // unknown / malformed token (e.g. lowercase, 'Z')
        const len = TOKEN_WIDTH[token];
        slices.push({ token, offset, len });
        offset += len;
        i += token.length;
    }
    // Grammar checks — reject ambiguous / under- / over-specified patterns.
    const counts = new Map();
    for (const s of slices)
        counts.set(s.token, (counts.get(s.token) ?? 0) + 1);
    for (const [, c] of counts)
        if (c > 1)
            return null; // any duplicate token is ambiguous
    const hasYYYY = counts.has("YYYY");
    const hasYY = counts.has("YY");
    const hasC = counts.has("C");
    const hasMM = counts.has("MM");
    const hasDD = counts.has("DD");
    const hasJJJ = counts.has("JJJ");
    if (!hasYYYY && !hasYY)
        return null; // must have a year source
    if (hasYYYY && hasYY)
        return null; // can't have both
    if (hasC && !hasYY)
        return null; // century flag only pairs with a 2-digit year
    if (hasJJJ && (hasMM || hasDD))
        return null; // Julian is exclusive of month/day
    if (!hasJJJ && (!hasMM || !hasDD))
        return null; // non-Julian needs both MM and DD
    if (hasJJJ && !(hasYYYY || hasYY))
        return null; // (covered above, defensive)
    return { width: offset, slices };
}
/**
 * Parse a keyed value against a custom token pattern → ISO yyyy-MM-dd, or null.
 * Non-digits are stripped first (separators allowed). The pattern is validated
 * against the token grammar (`planCustomPattern`); an invalid pattern → null.
 */
export function parseCustomDatePattern(raw, pattern, pivot = DEFAULT_CENTURY_PIVOT) {
    const plan = planCustomPattern(pattern);
    if (!plan)
        return null;
    const digits = (raw ?? "").replace(/\D/g, "");
    if (digits.length !== plan.width)
        return null;
    let century = null; // from a C flag, if present
    let yy = null;
    let yyyy = null;
    let mm = null;
    let dd = null;
    let jjj = null;
    for (const s of plan.slices) {
        const chunk = digits.slice(s.offset, s.offset + s.len);
        const n = Number(chunk);
        switch (s.token) {
            case "C":
                if (chunk !== "0" && chunk !== "1")
                    return null;
                century = chunk === "1" ? 2000 : 1900;
                break;
            case "YY":
                yy = n;
                break;
            case "YYYY":
                yyyy = n;
                break;
            case "MM":
                mm = n;
                break;
            case "DD":
                dd = n;
                break;
            case "JJJ":
                jjj = n;
                break;
        }
    }
    const year = yyyy != null
        ? yyyy
        : century != null && yy != null
            ? century + yy
            : yy != null
                ? pivotYear(yy, pivot)
                : null;
    if (year == null)
        return null;
    if (jjj != null)
        return julianToIso(year, jjj);
    if (mm != null && dd != null)
        return assembleIso(year, mm, dd);
    return null;
}
/** True when a custom token pattern passes the closed-grammar gate (E-025).
 *  The single validity check shared by the editor's commit + preview paths —
 *  free-text never persists unless this returns true. */
export function isValidCustomDatePattern(pattern) {
    return planCustomPattern(pattern) !== null;
}
/**
 * Config-time helper (E-025 Slice 2): describe a custom pattern by parsing a
 * canonical sample date (2024-03-15, day-of-year 075) through it, so the field
 * editor can show a LIVE preview as the author types. Returns a human string;
 * for an invalid pattern returns an "Invalid pattern" message naming the issue.
 */
export function describeCustomDatePattern(pattern, pivot = DEFAULT_CENTURY_PIVOT) {
    const plan = planCustomPattern(pattern);
    if (!plan) {
        return "Invalid pattern — use only YYYY, YY, MM, DD, JJJ, C (no duplicates; needs a year and either DD or JJJ).";
    }
    // Build the sample digit string for 2024-03-15 (Julian day 075).
    const sample = plan.slices
        .map((s) => {
        switch (s.token) {
            case "YYYY": return "2024";
            case "YY": return "24";
            case "MM": return "03";
            case "DD": return "15";
            case "JJJ": return "075";
            case "C": return "1"; // 2000s
        }
    })
        .join("");
    const iso = parseCustomDatePattern(sample, pattern, pivot);
    return iso
        ? `Sample: keying "${sample}" → ${iso}`
        : "Invalid pattern — sample did not resolve to a valid date.";
}
function canonicalizeDecimal(v, std) {
    if ((std ?? "").toUpperCase() === "CENTS") {
        const cleaned = v.replace(/[^\d-]/g, "");
        if (!/^-?\d+$/.test(cleaned))
            return null;
        const neg = cleaned.startsWith("-");
        const abs = cleaned.slice(neg ? 1 : 0);
        if (abs.length === 0)
            return null;
        const cents = BigInt(abs);
        const dollars = cents / 100n;
        const rem = (cents % 100n).toString().padStart(2, "0");
        return `${neg ? "-" : ""}${dollars}.${rem}`;
    }
    // DECIMAL: strip currency symbols + thousands separators, keep digits / dot / sign.
    const cleaned = v.replace(/[^\d.-]/g, "");
    if (!/^-?(\d+(\.\d+)?|\.\d+)$/.test(cleaned))
        return null;
    return cleaned;
}
const BUILTIN_DEFAULTS = {
    date: "MMDDYYYY",
    decimal: "DECIMAL",
};
export function builtinDefault(dataType) {
    const fam = canonicalFamily(dataType);
    return fam === "none" ? null : BUILTIN_DEFAULTS[fam];
}
export function resolveKeyStandard(dataType, field, jobType, tenant) {
    const fam = canonicalFamily(dataType);
    if (fam === "none")
        return null;
    return (field?.keyStandard ??
        jobType?.keyStandards?.[fam] ??
        tenant?.keyStandards?.[fam] ??
        builtinDefault(dataType));
}
//# sourceMappingURL=key-standard.js.map